import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Zap, Layers, Ban, Package, Mail } from "lucide-react";

const CAL_LINK = "https://cal.com/kalza-studio-hdho6x";
const EMAIL = "hello@kalzastudio.com";

const LOGOS = [
  { src: "/images/brand-articulations.png", alt: "Brand Articulations" },
  { src: "/images/uefa.png", alt: "UEFA Champions League" },
  { src: "/images/pyszne.png", alt: "Pyszne.pl" },
  { src: "/images/coca-cola.png", alt: "Coca-Cola" },
  { src: "/images/lukkien.png", alt: "Lukkien" },
  { src: "/images/just-eat.png", alt: "Just Eat Takeaway" },
  { src: "/images/house-of-retouching.png", alt: "House of Retouching" },
  { src: "/images/samsung.png", alt: "Samsung" },
  { src: "/images/ben-jerrys.png", alt: "Ben & Jerry\'s" },
  { src: "/images/heineken.png", alt: "Heineken" },
];

/* Organic shaped image */
const OrganicImage = ({ src, alt, className = "" }) => (
  <div
    className={`aspect-[4/5] overflow-hidden max-w-[65%] lg:max-w-none ${className}`}
    style={{
      WebkitMaskImage: 'url(/images/mood/shape-mask.png)',
      maskImage: 'url(/images/mood/shape-mask.png)',
      WebkitMaskSize: '100% 100%',
      maskSize: '100% 100%',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    }}
  >
    <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
  </div>
);

export default function HomePage() {
  return (
    <main data-testid="home-page">
      {/* ═══ 1. HERO (dark) ═══ */}
      <section className="relative min-h-screen pt-24">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 min-h-[calc(100vh-6rem)] items-center py-12">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="relative order-2 lg:order-1">
              <OrganicImage src="/images/ola-hero.jpg" alt="Aleksandra Kałuża, Founder of KALZA Studio" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="order-1 lg:order-2">
              <span className="font-eyebrow text-white mb-6 block">BRAND STRATEGY & CREATIVE DIRECTION</span>
              <h1 className="mb-6">
                <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">THE CLARITY</span>
                <span className="font-display text-4xl md:text-5xl lg:text-6xl text-white block leading-[0.95]">FOUNDERS NEED</span>
                <span className="font-accent text-4xl md:text-5xl lg:text-6xl text-[#BE123C] block leading-[1.1]">to finally move their business</span>
              </h1>
              <p className="font-body text-base md:text-lg text-white/60 leading-relaxed mb-4 max-w-md">
                I turn stalled businesses into living brands. I give founders the clarity that unlocks their energy. They stop guessing, start deciding and fall back in love with their own business. The business starts moving again.
              </p>
              <p className="font-body text-xs text-white/40 mb-8 max-w-md">
                Trusted across founder-led brands and global creative work.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#BE123C] transition-colors duration-300">
                  BOOK A BRAND CLARITY CALL <ArrowUpRight size={16} />
                </a>
                <Link to="/about" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/60 font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:text-white hover:border-white/40 transition-colors duration-300">
                  About Aleksandra
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 2. LOGO CAROUSEL (white) ═══ */}
      <section className="py-10 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <p className="font-eyebrow text-center text-[#6B6560] mb-8">BRANDS I'VE WORKED WITH</p>
        </div>
        <div className="relative overflow-hidden">
          <div className="flex animate-logo-scroll gap-16 items-center px-8">
            {[...LOGOS, ...LOGOS, ...LOGOS].map((logo, i) => (
              <img key={i} src={logo.src} alt={logo.alt} className="flex-shrink-0 h-6 md:h-8 w-auto object-contain invert grayscale opacity-50 hover:opacity-80 transition-all duration-300 mix-blend-multiply" />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes logoScroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.33%); }
          }
          .animate-logo-scroll {
            animation: logoScroll 40s linear infinite;
            width: max-content;
          }
          .animate-logo-scroll:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* ═══ 3. THE REAL PROBLEM (Apple grey) ═══ */}
      <section className="py-24 md:py-32 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-6 block">THE REAL PROBLEM</span>
              <h2 className="mb-10">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">YOU OUTGREW YOUR BRAND</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-2">and your clients can feel it</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] max-w-lg">
                <p>People usually get how good your work is once they work with you. The problem is what happens before that. When someone lands on your website or finds you on Instagram, it is not clear enough. They do not get what you do, who it is for or why they should care, so they move on.</p>
                <p className="text-[#4A4540]">Your business is better than the way it comes across online. This gap is costing you time, money and the kind of clients you actually want.</p>
                <p className="font-accent text-[#BE123C] text-lg mt-6">Sounds familiar? You are probably one of the three founders below.</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center space-y-10">
              {[
                { title: "People visit your site and leave", body: "Your brand may look fine, but it is not giving people a reason to stay." },
                { title: "Charging more feels like a fight", body: "When the value is not clear, every pricing conversation gets harder." },
                { title: "The clients you want walk right past", body: "Not because they are wrong for you, but because the fit is not obvious enough." },
              ].map((card, i) => (
                <div key={i} className="border-l-2 border-[#BE123C] pl-6">
                  <h3 className="font-body text-base font-semibold text-[#1C1210] mb-1.5">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 4. WHO THIS IS FOR (white) ═══ */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">WHO THIS IS FOR</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">FOR FOUNDERS STUCK BETWEEN</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">who they are</span>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95] mt-1">AND HOW THEIR BRAND SHOWS UP</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "The Founder At The Start", body: "You know what you do and you are good at it. But when it comes to talking about it or showing it online, the words feel generic and the visuals look like everyone else in your niche. The brand does not sound like you yet.", img: "/images/mood/arch-sea.jpg" },
              { title: "The Founder In Transition", body: "You have grown, changed and got clearer on what matters to you. But the brand still sounds like an older version of your life. You are building from a different place now, more honest and intentional.", img: "/images/mood/water-light.jpg" },
              { title: "The Established Founder", body: "Your business has real traction. People come back, recommend you and trust your work. You are ready to raise your prices. But your website and Instagram do not reflect the value you actually give.", img: "/images/mood/wood-amber.jpg" },
            ].map((card, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }} className="overflow-hidden">
                <div className="h-64 md:h-72 overflow-hidden">
                  <img src={card.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="pt-6 pb-2">
                  <h3 className="font-serif text-lg text-[#1C1210] mb-3">{card.title}</h3>
                  <p className="font-body text-sm text-[#6B6560] leading-relaxed">{card.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 5. MEET THE FOUNDER (image bg) ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <OrganicImage src="/images/ola-about.jpg" alt="Aleksandra Kałuża" className="shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">MEET THE FOUNDER</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">HEY, I'M</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">Aleksandra</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#4A4540] leading-[1.7]">
                <p>I'm a creative director, entrepreneur and photographer. I spent years leading global creative work inside international companies, working with brands like Just Eat Takeaway, Heineken, Coca-Cola, Ben & Jerry's, Samsung and UEFA Champions League.</p>
                <p>My job was not only about making things look good. I was paying close attention to how people respond to brands and the exact moments trust is built or lost. Now I do the same for founders building their own businesses.</p>
              </div>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "15+ years in brand and creative work",
                  "100+ brands across global and founder-led businesses",
                  "Led creative teams of 40+ people across Europe",
                  "Multi-million-euro campaigns for Heineken, Samsung and others",
                ].map((proof, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-1 h-1 rounded-full bg-[#BE123C] flex-shrink-0 mt-2" />
                    <span className="font-body text-xs text-[#6B6560]">{proof}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 mt-8 hover:bg-[#9F1239] transition-colors duration-300">
                LEARN MORE <ArrowUpRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 6. MY APPROACH (DARK — crescendo) ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-white mb-6 block">MY APPROACH</span>
              <h2 className="mb-8">
                <span className="font-display text-3xl md:text-4xl text-white block leading-[0.95]">I SEE WHAT'S THERE</span>
                <span className="font-accent text-3xl md:text-4xl text-[#E11D48] block leading-[1.1] mt-1">before it's said</span>
              </h2>
              <p className="font-body text-base text-white/70 leading-[1.7]">
                The way I work comes from years of reading brands from the inside out, leading creative work and paying close attention to what makes people trust fast. I dig into human behaviour, archetypes, narrative psychology and semiotics to get to the real issue. I do not follow a fixed template. Every business gets its own process, built around what is actually true about them.
              </p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="flex flex-col justify-center space-y-8">
              {[
                { Icon: Zap, title: "I spot the disconnect fast.", body: "Most founders know something is off. I can usually name it within the first conversation." },
                { Icon: Layers, title: "I go deeper than most.", body: "Archetypes, buyer psychology, narrative structure, semiotics. I use what works." },
                { Icon: Ban, title: "No fixed templates.", body: "Every business gets its own process, built around what is actually true about them." },
                { Icon: Package, title: "The result is practical.", body: "Words that hook, visuals that carry the heart of the business and a brand book that holds it all together." },
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-4">
                  <point.Icon size={18} className="text-[#E11D48] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-body text-sm font-semibold text-white mb-0.5">{point.title}</h3>
                    <p className="font-body text-sm text-white/60 leading-relaxed">{point.body}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 7. WORK WITH ME (white) — top-aligned grid ═══ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">FOUR WAYS IN</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WORK WITH ME</span>
              </h2>
              <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-lg mb-6">
                Direct work between you and me. Pick what matches where your business is right now, from a quick diagnostic to a full brand system.
              </p>
              <div className="space-y-2 mb-8">
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">
                  <span className="font-semibold text-[#1C1210]">Start here if</span> you want someone to name what is off in your brand, show you what to do about it or build the whole thing with you.
                </p>
                <p className="font-body text-sm text-[#9A9590] leading-relaxed italic">
                  Not a fit if you are looking for a logo alone or want to hand it all off without strategic conversations.
                </p>
              </div>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                SEE ALL SERVICES <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="space-y-5">
                {[
                  { label: "FOR CLARITY", title: "Brand Reality Check Audit", duration: "7 days" },
                  { label: "FOR POSITIONING", title: "Brand Foundation", duration: "2 to 4 weeks" },
                  { label: "FOR FULL EXPRESSION", title: "Full Brand System", duration: "~2 months" },
                  { label: "FOR A QUICK CONVERSATION", title: "1:1 Consultation", duration: "90 min" },
                ].map((s, i) => (
                  <div key={i} className="flex items-start justify-between gap-6 border-b border-[#1C1210]/10 pb-5 last:border-b-0">
                    <div>
                      <span className="inline-block font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-1.5">{s.label}</span>
                      <h3 className="font-serif text-lg text-[#1C1210]">{s.title}</h3>
                    </div>
                    <span className="font-body text-xs text-[#6B6560]/70 uppercase tracking-[1px] whitespace-nowrap mt-1.5">{s.duration}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 8. AI PRODUCTS (Apple grey) — top-aligned grid ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">FOUR AI AGENTS</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">AI PRODUCTS</span>
              </h2>
              <p className="font-body text-base text-[#6B6560] leading-[1.7] max-w-lg mb-6">
                Small AI tools built from the same brand thinking I use with 1:1 clients. One-time purchase, works in Claude or ChatGPT, yours forever.
              </p>
              <div className="space-y-2 mb-8">
                <p className="font-body text-sm text-[#4A4540] leading-relaxed">
                  <span className="font-semibold text-[#1C1210]">Start here if</span> you want to do the work yourself with a tool that has real brand thinking baked in.
                </p>
                <p className="font-body text-sm text-[#9A9590] leading-relaxed italic">
                  Not a fit if you are looking for someone to do the strategic work for you. For that see the services above.
                </p>
              </div>
              <Link to="/ai-products" className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300">
                EXPLORE AI PRODUCTS <ArrowRight size={14} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="space-y-5">
                {[
                  { label: "CONTENT AUDIT", title: "Make Me Memorable", price: "€29" },
                  { label: "INSTAGRAM STORIES", title: "Stories That Sell", price: "€49" },
                  { label: "IDENTITY DIAGNOSTIC", title: "Brand Blind Spots", price: "€69" },
                  { label: "PERSONAL AI AGENT", title: "DNA Voice", price: "€97" },
                ].map((p, i) => (
                  <div key={i} className="flex items-start justify-between gap-6 border-b border-[#1C1210]/10 pb-5 last:border-b-0">
                    <div>
                      <span className="inline-block font-eyebrow text-[10px] tracking-[2px] text-[#8B1A3A] mb-1.5">{p.label}</span>
                      <h3 className="font-serif text-lg text-[#1C1210]">{p.title}</h3>
                    </div>
                    <span className="font-body text-sm text-[#1C1210] font-medium whitespace-nowrap mt-1.5">{p.price}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 9. TESTIMONIAL (kalza-bg image) ═══ */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/kalza-bg.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
            <span className="font-eyebrow text-[#8B1A3A] mb-4 block">THE PROOF</span>
            <h2>
              <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">WHAT FOUNDERS SAY</span>
              <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">after working with me</span>
            </h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="max-w-5xl">
            <div className="bg-white p-8 md:p-12 relative border border-[#e5e2de] shadow-lg">
              <span className="absolute left-6 top-4 text-7xl font-serif text-[#BE123C]/10 select-none pointer-events-none leading-none">&ldquo;</span>
              <div className="relative space-y-4 font-body text-base md:text-lg text-[#4A4540] leading-relaxed max-w-3xl">
                <p>Before, I did not see much value in what I was doing. I was showing up for the paycheque, not the people.</p>
                <p>These workshops showed me what I actually offer, what my real strengths are and what I truly want to represent. My edge was never technical knowledge. It was my personality and the way I approach people.</p>
                <p>The work has colour again. Now I wake up eager to go to work, and I had not felt that in a long time.</p>
              </div>
              <p className="font-body text-sm text-[#9A9590] mt-8 tracking-wider uppercase">Grzegorz Galikowski, Finansy Founder</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ 10. NEWSLETTER (Apple grey) ═══ */}
      <section className="py-20 md:py-28 bg-[#f5f5f7]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <div className="aspect-[4/5] overflow-hidden bg-[#ebebed] max-w-[65%] lg:max-w-none">
                <img
                  src="/images/ola-newsletter.jpg"
                  alt="Aleksandra Kałuża"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}>
              <span className="font-eyebrow text-[#8B1A3A] mb-4 block">FOR FOUNDERS WHO'D RATHER KNOW</span>
              <h2 className="mb-6">
                <span className="font-display text-3xl md:text-4xl text-[#1C1210] block leading-[0.95]">DEAR FOUNDER,</span>
                <span className="font-accent text-3xl md:text-4xl text-[#BE123C] block leading-[1.1] mt-1">look closer.</span>
              </h2>
              <div className="space-y-4 font-body text-base text-[#6B6560] leading-[1.7] mb-8">
                <p>Your brand keeps talking after you leave the room. These letters are about what it says.</p>
                <p>Honest observations about founders and their brands. What is landing, what is not and why. No templates, no weekly noise.</p>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const emailInput = e.target.email;
                  const btn = e.target.querySelector('button');
                  const msg = e.target.querySelector('.form-msg');
                  btn.disabled = true;
                  btn.textContent = 'Sending...';
                  try {
                    const res = await fetch('https://connect.mailerlite.com/api/subscribers', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Authorization': 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiI0IiwianRpIjoiODIxOTY0NWMzMTE5NjE2YzczNjY3NDU5MTU5MGI2OWQyYTVlZDY2NjhmMWRmYWRjNWMxMTI3MDEyY2FmMjI4YWVmYTc0MzNmZmMzMjliYzkiLCJpYXQiOjE3Nzc0NTkyNTAuMjc1ODI2LCJuYmYiOjE3Nzc0NTkyNTAuMjc1ODI5LCJleHAiOjQ5MzMxMzI4NTAuMjcwMjg2LCJzdWIiOiIyMzAyODUyIiwic2NvcGVzIjpbXX0.HgZ-TwYNlT9vK2ssOiY_TVCxj5pZIsuwE_z7glvgbJpevnxbYLOYq7CTn9LVAsZowecF7IX-U7TWBNdT4pCOpQUZU2IfOFoydbmEJ420BaCKd_8bxxVVa-EjXc5hFAQxmds-bbzm3hGRjZXuBfkZPXe8L5PQNLO4O8oXSQZ1l3A0HcYHux4fsWSpQSlK_3KXYXR4UR6uBojDqFBZR1oqmmIDxSV41zJ-BONhS0XfP3yJ26cBrWYWGKvusOPFDyaV3OHHOGhIqOye0OLjEYITEefG4MM_RMCV_9nzi9CnUIcC3FlkPrd_YfMRgpgrb_TOEYROVXQNwsuqiyVv-VGwypCEKB6LuckybuUQCMojwFrFZqf-_uSM97GOZOSSaeE3TiR9vKP3lbGeWtGqrS3CPBi9DmVioWIHTh2WYKw5xVNETWIr-G5Xfsbna8LG6gclbLpKac6WMVkMgZhM_fL2bo_EaiXaMEJVMgb9szAgsyF_cQ3LuD2QZIC-Rt6tiu3FyEj2FIPzmq-hyiEDqC4IzTXT3xSZ92G5iERocoMBfkF4R9yRCjFmHX37drkuYksQ5kc_9-VV4c_8Ve_n3BYvbHnn0E3q6xF3lPHe8sOf75Qvd1Y4L2ipd3V4dcoOvtFxjym5JMu_m4EupXtELbV-xbw5Ne-fe6_Y5vQh_Rw0PRs',
                      },
                      body: JSON.stringify({
                        email: emailInput.value,
                        groups: ['186079588714022637'],
                      }),
                    });
                    if (res.ok) {
                      msg.textContent = 'You are in. Check your inbox.';
                      msg.className = 'form-msg font-body text-sm text-[#BE123C] mt-4';
                      emailInput.value = '';
                    } else {
                      msg.textContent = 'Something went wrong. Try again.';
                      msg.className = 'form-msg font-body text-sm text-[#9A9590] mt-4';
                    }
                  } catch {
                    msg.textContent = 'Something went wrong. Try again.';
                    msg.className = 'form-msg font-body text-sm text-[#9A9590] mt-4';
                  }
                  btn.disabled = false;
                  btn.textContent = 'Subscribe';
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="flex-1 border border-[#1C1210]/15 bg-white px-5 py-4 font-body text-sm text-[#1C1210] placeholder:text-[#6B6560]/60 focus:outline-none focus:border-[#BE123C] transition-colors duration-300"
                />
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#BE123C] text-white font-body font-medium text-xs tracking-[2px] uppercase px-8 py-4 hover:bg-[#9F1239] transition-colors duration-300"
                >
                  Subscribe <ArrowRight size={14} />
                </button>
              </form>
              <p className="form-msg font-body text-xs text-[#6B6560]/60 mt-4">
                No spam. Unsubscribe anytime.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ 11. FINAL CTA (dark) ═══ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/mood/dark-texture.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1C1210]/70" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <span className="font-eyebrow text-white mb-6 block">YOU HAVE SEEN HOW THIS PLAYS OUT</span>
            <h2 className="mb-8">
              <span className="font-display text-3xl md:text-5xl text-white block leading-[0.95]">NOW IT IS YOUR TURN</span>
              <span className="font-accent text-3xl md:text-5xl text-[#BE123C] block leading-[1.1] mt-2">to move your business</span>
            </h2>
            <p className="font-body text-base md:text-lg text-white/70 leading-relaxed mb-10 max-w-xl">
              If your business is stronger than the way it looks online right now, we should talk. Book a brand clarity call and I will tell you exactly what I see.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={CAL_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#E11D48] text-white font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:bg-[#BE123C] transition-colors duration-300">
                BOOK A BRAND CLARITY CALL <ArrowUpRight size={16} />
              </a>
              <a href={`mailto:${EMAIL}`} className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/70 font-body font-medium text-xs tracking-[2px] uppercase px-10 py-5 hover:text-white hover:border-white/40 transition-colors duration-300">
                <Mail size={14} /> {EMAIL}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
